import { Box, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useRef, useState } from 'react';

import { Spinner } from '@/components/common/Spinner';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { ApplicationAnswerProps } from '@/models/Application';
import { ADFPaymentRequest, PaymentType } from '@/models/Payment';
import { ProgramNames } from '@/models/Program';
import { QuestionProps } from '@/models/Question';
import { QuestionReviewProps } from '@/models/QuestionReview';
import {
  ApplicationStatus,
  treeLineCerficationFeeAmount,
  treeLineRecertificationFeeAmount,
} from '@/utils/constants';
import { YearsRecognizedByProgram } from '@/utils/YearsRecognizedUtils';

import PortalClosedBanner from './PortalClosedBanner';
import QuestionSwitch from './question_types/QuestionSwitch';
import Payment from '../payment_components/Payment';
import ApplicationButtons from './ApplicationButtons';

function ApplicationBody() {
  const appRef = useRef(null);

  const theme = useTheme();

  const {
    currentApplication,
    answers,
    currentStandardId,
    currentStandardInfo,
    canEdit,
    isLastStandard,
    partnerInfo,
  } = useContext(CurrentApplicationContext);

  const { portalOpen } = useContext(SelectedProgramContext);

  const [submitClicked, setSubmitClicked] = useState<boolean>(false);
  const [applicationComments, setApplicationComments] = useState<QuestionReviewProps[]>([]);

  useEffect(() => {
    if (answers == null) return;

    const questionReviews = Object.values(answers)
      .map((questionResponse: ApplicationAnswerProps | undefined) => {
        if (questionResponse == undefined) return [];
        return questionResponse.reviews;
      })
      ?.flat();

    setApplicationComments(questionReviews);
  }, [answers]);

  const [paymentRequest, setPaymentRequest] = useState({
    paymentAmount: 0,
    paymentType: PaymentType.CreditCard,
    creditCardToken: '',
    creditCardExpirationDate: '',
    billingContact: {
      name: '',
      address: '',
      city: '',
      region: '',
      postalCode: '',
      country: 'US',
    },
  } as ADFPaymentRequest);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  useEffect(() => {
    if (partnerInfo?.partnerYearsRecognized == null) return;

    const nextAmount =
      YearsRecognizedByProgram(partnerInfo.partnerYearsRecognized, ProgramNames.TreeLineUSA) === 0
        ? treeLineCerficationFeeAmount
        : treeLineRecertificationFeeAmount;

    setPaymentRequest((prev) => {
      if (prev.paymentAmount === nextAmount) return prev;
      return { ...prev, paymentAmount: nextAmount };
    });
  }, [partnerInfo]);

  const handlePaymentRequestChanged = (updatedPaymentRequest: ADFPaymentRequest) => {
    setPaymentRequest(updatedPaymentRequest);
  };

  return (
    <Box display='flex' flex={1} justifyContent='center' className='wrap' ref={appRef}>
      <Box display='flex' flexDirection='column' flexGrow={1} gap='16px'>
        <Box display='flex' alignItems='center' justifyContent='center'>
          <Typography variant='h2' textAlign={'center'}>
            {currentStandardInfo?.standardName?.toUpperCase()}
          </Typography>
        </Box>

        {currentStandardInfo?.standardDescription != null && (
          <Box display='flex' alignItems='center' justifyContent='center'>
            <div
              dangerouslySetInnerHTML={{
                __html: currentStandardInfo?.standardDescription,
              }}
            ></div>
          </Box>
        )}
        <Box
          display='flex'
          flexDirection='column'
          padding={5}
          sx={{ backgroundColor: theme.palette.sandShade1.main }}
          gap={2}
        >
          {currentStandardId != null ? (
            [...(currentStandardInfo?.questions ?? [])]
              .sort((a: QuestionProps, b: QuestionProps) => a.questionOrder - b.questionOrder)
              .map((q: QuestionProps) => {
                return (
                  <Box
                    className='question-wrap'
                    key={q.id}
                    sx={{
                      /*
                        Prevents hidden questions from leaving fragments behind that mess up layouts.
                        Ideally, this box should just not render. Unfortunately, the logic to determine if
                        a question should be hidden currently exists on each question type which is several
                        layers below this.
                      */
                      '&:has(> div:only-child:empty)': {
                        display: 'none',
                      },
                    }}
                  >
                    <Box flexBasis={'auto'}>
                      <QuestionSwitch key={q.id} question={q} />
                    </Box>
                  </Box>
                );
              })
          ) : (
            <Spinner type='circular' />
          )}

          {isLastStandard &&
            currentApplication != null &&
            currentApplication.programId === ProgramNames.TreeLineUSA &&
            currentApplication.status === ApplicationStatus.InProgress && (
              <Payment
                paymentRequest={paymentRequest}
                errorMessage={paymentError}
                onChange={handlePaymentRequestChanged}
                submitted={submitClicked}
              />
            )}
        </Box>
        {currentApplication != null &&
          currentApplication.applicationTypeId != null &&
          !portalOpen[currentApplication.applicationTypeId] &&
          !canEdit && (
            <PortalClosedBanner applicationTypeId={currentApplication.applicationTypeId} />
          )}
        <Box display='flex' justifyContent='center' gap='20px' paddingBottom={5}>
          <ApplicationButtons
            submitClicked={submitClicked}
            onSubmitClickedChange={setSubmitClicked}
            onPaymentError={setPaymentError}
            applicationComments={applicationComments}
            paymentRequest={paymentRequest}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default ApplicationBody;
